import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { checkUrgentTask, keepOnline } from "../services/trigger.service";
import { useCurrentPageStore } from "../stores/store";
import { InitialUserPageType } from "../types/global";

interface TaskCount {
  key_in: number;
  task_creator: number;
}

interface TriggerContextType {
  countYourTask: TaskCount;
}

const TriggerContext = createContext<TriggerContextType | undefined>(undefined);

function useIntervalEffect(
  callback: () => void,
  delay: number,
  deps: React.DependencyList,
) {
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay, ...deps]);
}

export const TriggerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const currentPage = useCurrentPageStore(
    (state) => (state as InitialUserPageType).currentPage,
  );
  const [countYourTask, setCountYourTask] = useState<TaskCount>({
    key_in: 0,
    task_creator: 0,
  });

  // Interval effect for keepOnline and checkUrgentTask
  useIntervalEffect(
    () => {
      keepOnline({ current_page: currentPage ?? "" });
      checkUrgentTask().then(setCountYourTask);
    },
    20000,
    [currentPage],
  );

  // Initial fetch on mount
  useEffect(() => {
    checkUrgentTask().then(setCountYourTask);
  }, []);

  const contextValue = useMemo(() => ({ countYourTask }), [countYourTask]);

  return (
    <TriggerContext.Provider value={contextValue}>
      {children}
    </TriggerContext.Provider>
  );
};

export function useTrigger() {
  const ctx = useContext(TriggerContext);
  if (!ctx) throw new Error("useTrigger must be used within a TriggerProvider");
  return ctx;
}
